import React, { useMemo, useState } from "react";
import { Collapse, Checkbox, DatePicker, Typography } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHook";
import { setFilters } from "../../reducers/catalogFilterSlice";
import { CatalogFilterState } from "../../types";
import dayjs from "dayjs";
import "./index.css";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { SpeciesList } from "../../utils/const";
import { toggleSpeciesFilter } from "../../utils/helpers";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import ChevronUpIcon from "@heroicons/react/24/outline/ChevronUpIcon";

const { Panel } = Collapse;
const { Text } = Typography;

const imageTypeOptions = [
  { label: <Text>Mosquito images</Text>, value: "mosquito" },
  { label: <Text>Mesh images</Text>, value: "mesh" },
  { label: <Text>Empty mesh images</Text>, value: "emptyMesh" },
  { label: <Text>Bycatch images</Text>, value: "bycatch" },
  { label: <Text>Debris images</Text>, value: "debris" },
  { label: <Text>Bookmarked images</Text>, value: "bookmarked" },
];

const CatalogFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state) => state.catalogFilter);
  const [localDateAfter, setLocalDateAfter] = useState<string | null>(
    filters.capturedAfter,
  );
  const [localDateBefore, setLocalDateBefore] = useState<string | null>(
    filters.capturedBefore,
  );

  const { data: devices } = useListDevicesQuery();
  const { data: trapSites } = useListTrapSitesQuery();

  const deviceOptions = useMemo(
    () =>
      devices?.items?.map((device) => ({
        label: device?.display_name,
        value: device?.uuid ?? "",
      })) ?? [],
    [devices],
  );

  const trapSitesOptions = useMemo(() => {
    const options =
      trapSites?.items?.map((trapSite) => ({
        label: trapSite?.display_name,
        value: trapSite?._id ?? "",
      })) ?? [];
    options.push({ label: "Unassigned Location", value: "unassigned" });
    return options;
  }, [trapSites]);

  const handleSpeciesChange = (genus: string, speciesName?: string) => {
    const genusEntry = SpeciesList.find((g) => g.genus === genus);
    const allSpeciesForGenus = genusEntry?.species ?? [];
    const newSpecies = toggleSpeciesFilter(
      filters.species,
      genus,
      speciesName,
      allSpeciesForGenus,
    );
    dispatch(setFilters({ species: newSpecies }));
  };

  const handleDateChange =
    (field: "capturedAfter" | "capturedBefore") =>
    (date: dayjs.Dayjs | null) => {
      let finalDate = date;

      if (date && field === "capturedBefore") {
        finalDate = date.endOf("day");
      }
      const value = finalDate ? finalDate.toISOString() : null;

      if (field === "capturedAfter") {
        setLocalDateAfter(value);
      } else {
        setLocalDateBefore(value);
      }

      const after = field === "capturedAfter" ? value : localDateAfter;
      const before = field === "capturedBefore" ? value : localDateBefore;

      const isComplete = after && before;
      const isCleared = !after && !before;

      if (isComplete || isCleared) {
        dispatch(setFilters({ capturedAfter: after, capturedBefore: before }));
      }
    };

  const handleImageTypeChange = (checkedValues: string[]) => {
    const newImageTypes = Object.fromEntries(
      imageTypeOptions.map(({ value }) => [
        value,
        checkedValues.includes(value),
      ]),
    ) as CatalogFilterState["imageTypes"];
    dispatch(setFilters({ imageTypes: newImageTypes }));
  };

  return (
    <div className="catalog-filters-container">
      <div className="catalog-filters-title">
        <span>Filters</span>
      </div>
      <Collapse
        ghost
        defaultActiveKey={["date"]}
        expandIconPosition="end"
        expandIcon={({ isActive }) =>
          isActive ? (
            <ChevronUpIcon width={24} height={24} />
          ) : (
            <ChevronDownIcon width={24} height={24} />
          )
        }
      >
        <Panel
          header={
            <div className="flex flex-col">
              <div>Date range</div>
            </div>
          }
          key="date"
          collapsible="icon"
          showArrow={false}
        >
          <div className="date-range-inputs">
            <DatePicker
              placeholder="Start Date"
              value={localDateAfter ? dayjs(localDateAfter) : null}
              onChange={handleDateChange("capturedAfter")}
              disabledDate={(current) =>
                localDateBefore
                  ? current.isAfter(dayjs(localDateBefore), "day")
                  : false
              }
            />
            <DatePicker
              placeholder="End Date"
              value={localDateBefore ? dayjs(localDateBefore) : null}
              onChange={handleDateChange("capturedBefore")}
              disabledDate={(current) =>
                localDateAfter
                  ? current.isBefore(dayjs(localDateAfter), "day")
                  : false
              }
            />
          </div>
          {(localDateAfter && !localDateBefore) ||
          (!localDateAfter && localDateBefore) ? (
            <Text className="text-[#EF4444]! text-xs!">
              Both start and end date must be selected.
            </Text>
          ) : null}
        </Panel>

        <Panel header="Species" key="species">
          {SpeciesList.map(({ genus, species }) => (
            <div key={genus} className="species-genus-item">
              <Collapse
                ghost
                expandIconPosition="end"
                expandIcon={({ isActive }) =>
                  isActive ? <MinusOutlined /> : <PlusOutlined />
                }
                className="species-genus-item-collapse"
              >
                <Panel
                  header={
                    <Checkbox
                      checked={filters.species[genus]?.selected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSpeciesChange(genus);
                      }}
                    >
                      <Text>{genus}</Text>
                    </Checkbox>
                  }
                  key={genus}
                >
                  <div className="species-list">
                    {species.map((s) => (
                      <div key={s}>
                        <Checkbox
                          checked={filters.species[genus]?.species[s]}
                          onChange={() => handleSpeciesChange(genus, s)}
                        >
                          <Text>{`${genus} ${s}`}</Text>
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                </Panel>
              </Collapse>
            </div>
          ))}
          {["Undefined", "Unknown"].map((label) => (
            <div key={label} className="species-genus-item-special">
              <Checkbox
                checked={filters.species[label]?.selected}
                onChange={() => handleSpeciesChange(label)}
              >
                <Text>{label}</Text>
              </Checkbox>
            </div>
          ))}
        </Panel>

        <Panel header="Image types" key="imageTypes">
          <Checkbox.Group
            options={imageTypeOptions}
            value={Object.entries(filters.imageTypes)
              .filter(([_, checked]) => checked)
              .map(([key]) => key)}
            onChange={handleImageTypeChange}
            className="check-box-image-type"
          />
        </Panel>

        <Panel header="Locations" key="locations">
          <div className="list-container">
            {trapSitesOptions.map((trapSite) => (
              <div key={trapSite.value}>
                <Checkbox
                  checked={filters.trapsites.includes(trapSite.value)}
                  onChange={(e) => {
                    const newLocs = e.target.checked
                      ? [...filters.trapsites, trapSite.value]
                      : filters.trapsites.filter((l) => l !== trapSite.value);
                    dispatch(setFilters({ trapsites: newLocs }));
                  }}
                >
                  <Text>{trapSite.label}</Text>
                </Checkbox>
              </div>
            ))}
          </div>
        </Panel>

        <Panel header="Scout Devices" key="devices">
          <div className="list-container">
            {deviceOptions.map((device) => (
              <div key={device.value}>
                <Checkbox
                  checked={filters.devices.includes(device.value)}
                  onChange={(e) => {
                    const newDevs = e.target.checked
                      ? [...filters.devices, device.value]
                      : filters.devices.filter((d) => d !== device.value);
                    dispatch(setFilters({ devices: newDevs }));
                  }}
                >
                  <Text>{device.label}</Text>
                </Checkbox>
              </div>
            ))}
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};

export default CatalogFilters;
